import AssistChip from 'LuxKit/Chips/AssistChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import CounterInput from 'components/Common/Form/Input/CounterInput'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import OfferListFilterSidebar from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import type { EnabledFilters } from 'components/Search/type'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { updateRoomFilterSearch } from '../../HotelSearchPage/utils/updateRoomFilterSearch'
import type { OfferListSortOption } from 'constants/offerListFilters'

const Container = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${rem(16)};
`

const ControlsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(16)};
`

const QuickRoomFiltersContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${rem(16)};
`

const QuickRoomCounterInput = styled(CounterInput).attrs({
  horizontalAlign: 'center',
  tabletHorizontalAlign: 'center',
})`
  align-items: center;
  gap: ${rem(4)};

  > div {
    text-align: center;
  }

  .variant-label {
    font-weight: ${props => props.theme.font.primary.weight.regular};
  }
`

interface Props {
  filters: App.OfferListFilters
  enabledFilters?: EnabledFilters
  sortOptions?: Array<OfferListSortOption>
  windowSearch: string
  onFilterChange: FilterControlChangeHandler
  onSortChange: SortControlChangeHandler
}

function HomesAndVillasSplitMapFilterBar({
  filters,
  enabledFilters,
  sortOptions,
  windowSearch,
  onFilterChange,
  onSortChange,
}: Props) {
  const intl = useIntl()
  const currentHash = useAppSelector(state => state.router.location.hash)
  const { isFilterPanelOpen, toggleFilterPanel } = useGlobalSearchURLHashFilter()

  const filtersText = intl.formatMessage({
    id: 'pr1whn7-wNBM8zXkHG64',
    defaultMessage: 'Filters',
    description: 'Button label to open the search filters panel in the search results interface',
  })

  const bedroomsLabel = intl.formatMessage({
    id: '6Q4qMGSm10vQ4Zt1sbZH',
    defaultMessage: 'Bedrooms',
    description: 'Label for bedrooms counter input on the hotel or room booking page',
  })

  const bedsLabel = intl.formatMessage({
    id: 'fnQIS-HL6BAaLp8XgjZg',
    defaultMessage: 'Beds',
    description: 'Form field label for the number of beds counter input in the room configuration',
  })

  const bathroomsLabel = intl.formatMessage({
    id: 'wN1qfz60XrDsoWe5U4MW',
    defaultMessage: 'Bathrooms',
    description: 'Label for bathrooms counter input on the hotel or room booking page',
  })

  const anyLabel = intl.formatMessage({
    id: '1JUG8N8aWPV_FlrH5SA2',
    defaultMessage: 'Any',
    description: 'Display label for a room filter counter value of zero',
  })

  const formatCounterValue = useCallback((value: number) => {
    return value === 0 ? anyLabel : `${value}+`
  }, [anyLabel])

  const onRoomCounterChange = useCallback((filterKey: 'bedrooms' | 'beds' | 'bathrooms', value: number) => {
    const updatedSearch = updateRoomFilterSearch(windowSearch, filterKey, value)
    onFilterChange({ search: updatedSearch, hash: currentHash })
  }, [windowSearch, onFilterChange, currentHash])

  const onBedroomsChange = useCallback((value: number) => {
    onRoomCounterChange('bedrooms', value)
  }, [onRoomCounterChange])

  const onBedsChange = useCallback((value: number) => {
    onRoomCounterChange('beds', value)
  }, [onRoomCounterChange])

  const onBathroomsChange = useCallback((value: number) => {
    onRoomCounterChange('bathrooms', value)
  }, [onRoomCounterChange])

  return (
    <>
      <Container>
        <ControlsContainer>
          <AssistChip
            size="medium"
            variant="filled"
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
          >
            {filtersText}
          </AssistChip>
          {!!enabledFilters && (
            <QuickRoomFiltersContainer>
              {enabledFilters.bedrooms && (
                <QuickRoomCounterInput
                  name="bedroomsGte"
                  value={filters.bedroomsGte ?? filters.bedroomsEq ?? 0}
                  onChange={onBedroomsChange}
                  min={0}
                  max={10}
                  size="small"
                  label={bedroomsLabel}
                  direction="vertical"
                  tabletDirection="vertical"
                  valueFormatter={formatCounterValue}
                />
              )}
              {enabledFilters.beds && (
                <QuickRoomCounterInput
                  name="bedsGte"
                  value={filters.bedsGte ?? filters.bedsEq ?? 0}
                  onChange={onBedsChange}
                  min={0}
                  max={10}
                  size="small"
                  label={bedsLabel}
                  direction="vertical"
                  tabletDirection="vertical"
                  valueFormatter={formatCounterValue}
                />
              )}
              {enabledFilters.bathrooms && (
                <QuickRoomCounterInput
                  name="bathroomsGte"
                  value={filters.bathroomsGte ?? filters.bathroomsEq ?? 0}
                  onChange={onBathroomsChange}
                  min={0}
                  max={10}
                  size="small"
                  label={bathroomsLabel}
                  direction="vertical"
                  tabletDirection="vertical"
                  valueFormatter={formatCounterValue}
                />
              )}
            </QuickRoomFiltersContainer>
          )}
        </ControlsContainer>
        {!!sortOptions?.length && (
          <OfferSortControl
            sortOptions={sortOptions}
            roundedButton
            dropdownPlacement="bottom-end"
            onSortChange={onSortChange}
          />
        )}
      </Container>
      {!!enabledFilters && (
        <OfferListFilterSidebar
          open={isFilterPanelOpen}
          onClose={toggleFilterPanel}
          filters={filters}
          sortOptions={sortOptions}
          search={windowSearch}
          enabledFilters={enabledFilters}
          onFilterChange={onFilterChange}
          showRoomsAndAmenitiesAtTop
        />
      )}
    </>
  )
}

export default HomesAndVillasSplitMapFilterBar
